import type { FormEmbedConfig, FormEmbedField, FormEmbedNavigation, FormEmbedScreen } from "./types.js";

/**
 * Form branching for the embed.
 *
 * Graph forms route visitors by their answers: "I'm a DIY" leads to "I need
 * help measuring", other answers skip it. The embed shows its fields all at
 * once, so instead of navigating it walks the form's graph from the entry
 * screen with the answers given so far and shows only the fields whose screen
 * the walk reaches. Mirrors the widget's `getNextScreen` in formvu-web
 * (`src/shared/widget/utils/graph-navigation.ts`) and its condition evaluator
 * (`src/lib/graph/graph-utils.ts`) — keep them in sync.
 *
 * Screens the embed doesn't show are walked through too. Their questions are
 * unanswered, so the walk keeps only what every branch from them leads to. A
 * branch screen picked without the question that leads to it is therefore
 * never reached — the form author has to include that question.
 */

type Answers = Record<string, unknown>;

interface ConditionLeaf {
  path?: unknown;
  operator?: unknown;
  value?: unknown;
}

interface ConditionGroup {
  all?: unknown[];
  any?: unknown[];
}

function resolvePath(data: Answers, path: unknown): unknown {
  if (typeof path !== "string") return undefined;
  const clean = path.startsWith("$.") ? path.substring(2) : path;
  return clean
    .split(".")
    .reduce<unknown>(
      (obj, key) => (obj !== null && obj !== undefined ? (obj as Answers)[key] : undefined),
      data,
    );
}

/** A condition's outcome; `undefined` while it reads a question nobody has answered yet. */
type Outcome = boolean | undefined;

function evaluateLeaf(leaf: ConditionLeaf, answers: Answers): Outcome {
  if (typeof leaf.path !== "string" || typeof leaf.operator !== "string") return false;

  const value = resolvePath(answers, leaf.path);
  if (value === undefined) return undefined;

  switch (leaf.operator) {
    case "equal":
      return value === leaf.value;
    case "notEqual":
      return value !== leaf.value;
    case "in":
      return Array.isArray(leaf.value) && leaf.value.includes(value);
    case "notIn":
      return Array.isArray(leaf.value) && !leaf.value.includes(value);
    case "greaterThan":
      return typeof value === "number" && value > (leaf.value as number);
    case "greaterThanInclusive":
      return typeof value === "number" && value >= (leaf.value as number);
    case "lessThan":
      return typeof value === "number" && value < (leaf.value as number);
    case "lessThanInclusive":
      return typeof value === "number" && value <= (leaf.value as number);
    case "contains":
      return Array.isArray(value) && value.includes(leaf.value);
    case "doesNotContain":
      return Array.isArray(value) && !value.includes(leaf.value);
    default:
      return false;
  }
}

/**
 * json-rules-engine `all`/`any` condition, decided as far as the answers allow:
 * `false` once an answered part rules it out, `true` once the answered parts
 * settle it, `undefined` while it still hangs on an unanswered question.
 * Never throws — a malformed condition is `false`.
 */
function conditionOutcome(condition: unknown, answers: Answers): Outcome {
  if (condition === null || typeof condition !== "object" || Array.isArray(condition)) return false;

  const group = condition as ConditionGroup;
  if (!("all" in group || "any" in group)) return evaluateLeaf(condition as ConditionLeaf, answers);

  let all: Outcome = true;
  if (Array.isArray(group.all)) {
    for (const child of group.all) {
      const outcome = conditionOutcome(child, answers);
      if (outcome === false) return false;
      if (outcome === undefined) all = undefined;
    }
  }
  let any: Outcome = true;
  if (Array.isArray(group.any)) {
    any = false;
    for (const child of group.any) {
      const outcome = conditionOutcome(child, answers);
      if (outcome === true) {
        any = true;
        break;
      }
      if (outcome === undefined) any = undefined;
    }
  }
  if (any === false) return false;
  return all === true && any === true ? true : undefined;
}

/** json-rules-engine `all`/`any` condition. Never throws — a malformed condition doesn't match. */
export function evaluateCondition(condition: unknown, answers: Answers): boolean {
  return conditionOutcome(condition, answers) === true;
}

/**
 * The screens a visitor may go to next, in the widget's edge order: the first
 * matching conditional edge, else the default. While a condition hangs on an
 * unanswered question, every route still open is listed — that edge, and the
 * ones after it. A route that would dead-end is left out: the visitor answers
 * so that some edge matches.
 */
function nextScreens(screenId: string, navigation: FormEmbedNavigation, answers: Answers): string[] {
  const outgoing = navigation.edges
    .filter((e) => e.sourceScreenId === screenId && e.label !== "skip")
    .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));

  const open = new Set<string>();
  let fallback: string | null = null;
  for (const edge of outgoing) {
    if (!edge.condition) {
      fallback ??= edge.targetScreenId;
      continue;
    }
    const outcome = conditionOutcome(edge.condition, answers);
    if (outcome === true) {
      // Matches unless an earlier, undecided edge matches first.
      open.add(edge.targetScreenId);
      return [...open];
    }
    if (outcome === undefined) open.add(edge.targetScreenId);
  }
  if (fallback) open.add(fallback);
  return [...open];
}

/**
 * Ids of the screens a visitor with these answers is sure to pass through.
 *
 * Where the route hangs on an unanswered question, only the screens every
 * open branch leads to count: the paths may converge on the same questions
 * later (both answers lead on to the address), while a screen on one branch
 * alone waits for the answer that picks it.
 *
 * Solved as a fixpoint over the reachable screens rather than by walking
 * every path, which would double the work at each unanswered split.
 */
export function reachableScreenIds(navigation: FormEmbedNavigation, answers: Answers): Set<string> {
  // Every screen the visitor may still reach, with its open routes.
  const routes = new Map<string, string[]>();
  const queue = [navigation.entryScreenId];
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (routes.has(id)) continue;
    const next = nextScreens(id, navigation, answers);
    routes.set(id, next);
    queue.push(...next);
  }

  // A route counts only if the form can end along it — a loop with no way
  // out is a dead end, not a path every visitor takes. A screen with nothing
  // but such routes (a malformed graph) keeps them all, so nothing is hidden.
  const ending = new Set([...routes].filter(([, next]) => next.length === 0).map(([id]) => id));
  for (let grew = true; grew; ) {
    grew = false;
    for (const [id, next] of routes) {
      if (!ending.has(id) && next.some((target) => ending.has(target))) {
        ending.add(id);
        grew = true;
      }
    }
  }

  // must(s) = {s} ∪ ⋂ must(t) over s's open routes, from "everything" down
  // to the greatest fixpoint: the screens on every way to the end. Each pass
  // only shrinks the sets, so it settles within as many passes as screens.
  const ids = [...routes.keys()];
  const must = new Map(ids.map((id) => [id, new Set(ids)]));
  for (let changed = true; changed; ) {
    changed = false;
    for (const id of ids) {
      const next = routes.get(id)!;
      const ends = next.filter((target) => ending.has(target));
      const [first, ...rest] = (ends.length > 0 ? ends : next).map((target) => must.get(target)!);
      const result = new Set([id]);
      for (const screen of first ?? []) {
        if (rest.every((branch) => branch.has(screen))) result.add(screen);
      }
      if (result.size !== must.get(id)!.size) {
        must.set(id, result);
        changed = true;
      }
    }
  }

  return must.get(navigation.entryScreenId)!;
}

/**
 * Answers as the widget would hold them: blank inputs are unanswered (an empty
 * string would satisfy `notEqual`) and number inputs are numbers.
 */
function toAnswers(values: Answers, fields: FormEmbedField[], prefill: Record<string, string>): Answers {
  const answers: Answers = { ...prefill };
  for (const field of fields) {
    const value = values[field.name];
    if (value !== "" && value !== undefined && value !== null) answers[field.name] = value;
    // Prefill arrives as strings too, so this covers both sources.
    const answer = answers[field.name];
    if (field.type === "number" && typeof answer === "string" && answer.trim() !== "") {
      const number = Number(answer);
      if (!Number.isNaN(number)) answers[field.name] = number;
    }
  }
  return answers;
}

/**
 * The builder preview splits a multi-question screen into one entry per field,
 * with ids `${screenId}:${index}`. Edges reference the screen itself.
 */
function graphScreenId(screen: FormEmbedScreen): string {
  return screen.id.split(":")[0];
}

/**
 * Names of the fields to show for the current answers. Every field shows for a
 * linear form. The phone field always shows: the submission can't go out
 * without it, whatever the graph says.
 */
export function visibleFieldNames(
  embedConfig: FormEmbedConfig,
  values: Answers,
  prefill: Record<string, string> = {},
): Set<string> {
  const { screens, navigation } = embedConfig;
  const fields = screens.map((s) => s.config.field);
  if (!navigation) return new Set(fields.map((f) => f.name));

  const reached = reachableScreenIds(navigation, toAnswers(values, fields, prefill));
  return new Set(
    screens
      .filter((s) => s.config.field.type === "phone" || reached.has(graphScreenId(s)))
      .map((s) => s.config.field.name),
  );
}
